"use client";

import { cn } from "@workspace/frontend-ui";
import HeroDots from "./HeroDots";
import HeroPlaylist from "./HeroPlaylist";
import HeroTrack from "./HeroTrack";
import useHeroCarousel from "./hooks/useHeroCarousel";
import type { HighlightedGameSlide } from "./types";

type Props = {
  slides: HighlightedGameSlide[];
  providerName: string;
  logoUrl?: string;
  totalGamesCount?: number;
  randomGameHrefs?: string[];
  providerGameThumbnailUrls?: string[];
  dir?: "ltr" | "rtl";
  translations: Record<string, string>;
};

const HeroCarousel: FC<Props> = ({
  slides,
  providerName,
  logoUrl,
  totalGamesCount,
  randomGameHrefs,
  providerGameThumbnailUrls,
  dir = "ltr",
  translations,
}) => {
  const {
    setApi,
    selectedIndex,
    progress,
    setProgress,
    handleComplete,
    handleSelect,
  } = useHeroCarousel();
  const hasSeveralSlides = slides.length > 1;

  return (
    <section
      data-testid="highlighted-games-hero"
      className="flex flex-col gap-3 md:flex-row max-sm:-m-1"
    >
      <div
        className={cn(
          "flex min-w-0 flex-col gap-3",
          hasSeveralSlides ? "flex-1" : "md:mx-auto md:w-[calc(100%-15.75rem)]",
        )}
      >
        {hasSeveralSlides && (
          <HeroDots
            slides={slides}
            selectedIndex={selectedIndex}
            progress={progress}
            onSelect={handleSelect}
          />
        )}
        <HeroTrack
          slides={slides}
          providerName={providerName}
          logoUrl={logoUrl}
          totalGamesCount={totalGamesCount}
          randomGameHrefs={randomGameHrefs}
          providerGameThumbnailUrls={providerGameThumbnailUrls}
          dir={dir}
          translations={translations}
          selectedIndex={selectedIndex}
          onApiChange={setApi}
          onProgress={hasSeveralSlides ? setProgress : undefined}
          onComplete={hasSeveralSlides ? handleComplete : undefined}
        />
      </div>
      {hasSeveralSlides && (
        <HeroPlaylist
          slides={slides}
          selectedIndex={selectedIndex}
          providerName={providerName}
          progress={progress}
          onSelect={handleSelect}
        />
      )}
    </section>
  );
};

export default HeroCarousel;
